import { Controller } from "@hotwired/stimulus"

// Heim-vs-24h-Pflege Kostenvergleich. Alle Daten kommen server-seitig als JSON
// (PflegekostenRechnerController#load_vergleich_data), gerechnet wird nur hier.
export default class extends Controller {
  static values = { data: Object, umgebungUrl: String }

  static targets = [
    "pflegegradButton",
    "pg1Note",
    "bundeslandSelect",
    "plzInput",
    "plzStatus",
    "quelle",
    "jahrRadio",
    "qualitaetRadio",
    "steuerCheckbox",
    "umwidmungCheckbox",
    "entlastungCheckbox",

    "resultHeim",
    "heimUebrige",
    "heimPflege",
    "heimLabel",
    "heimZuschlagSatz",
    "heimZuschlag",

    "resultH24",
    "h24Preis",
    "h24PflegegeldLabel",
    "h24Pflegegeld",
    "h24Verhinderung",
    "h24EntlastungRow",
    "h24Entlastung",
    "h24UmwidmungRow",
    "h24Umwidmung",
    "h24SteuerRow",
    "h24Steuer",

    "barHeim",
    "barH24",
    "delta",

    "resultSummary",
    "stickyBar",
    "stickyHeim",
    "stickyH24"
  ]

  connect() {
    this.formatter = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
    this.preselectBundesland()
    this.selectPflegegradValue(3)
    this.observeResultCard()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  // Phone sticky bar: visible whenever the result summary is off screen (it is lg:hidden on desktop)
  observeResultCard() {
    if (!this.hasStickyBarTarget || !this.hasResultSummaryTarget || !("IntersectionObserver" in window)) return
    this.observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting
      this.stickyBarTarget.classList.toggle("translate-y-full", !show)
      this.stickyBarTarget.inert = !show
    })
    this.observer.observe(this.resultSummaryTarget)
  }

  // Orts- und Anbieterseiten verlinken mit #bundesland=<slug> oder #plz=<plz>: Fragment statt
  // Query, damit Canonical, Edge-Cache und Linksignale auf der sauberen URL bleiben.
  preselectBundesland() {
    const params = new URLSearchParams(window.location.hash.slice(1))
    const slug = params.get("bundesland")
    if (slug && this.hasBundeslandSelectTarget) {
      const option = Array.from(this.bundeslandSelectTarget.options).find(o => o.value === slug)
      if (option) this.bundeslandSelectTarget.value = slug
    }
    const plz = params.get("plz")
    if (plz && /^\d{5}$/.test(plz) && this.hasPlzInputTarget) {
      this.plzInputTarget.value = plz
      this.lookupPlz(plz)
    }
  }

  // Manuelle Bundesland-Wahl hebt den PLZ-Umkreis auf
  bundeslandChanged() {
    if (this.region || this.plzInputTarget.value) {
      this.region = null
      this.plzInputTarget.value = ""
      this.plzStatusTarget.textContent = ""
    }
    this.calculate()
  }

  plzInput() {
    const plz = this.plzInputTarget.value.replace(/\D/g, "").slice(0, 5)
    this.plzInputTarget.value = plz
    if (plz.length === 5) return this.lookupPlz(plz)

    this.plzStatusTarget.textContent = ""
    if (this.region) {
      this.region = null
      this.calculate()
    }
  }

  async lookupPlz(plz) {
    this.plzStatusTarget.textContent = "Suche Pflegeheime in der Nähe …"
    let response
    try {
      response = await fetch(`${this.umgebungUrlValue}?plz=${plz}`, { headers: { Accept: "application/json" } })
    } catch {
      response = null
    }
    // Inzwischen andere PLZ getippt: Antwort verwerfen
    if (this.plzInputTarget.value !== plz) return

    if (!response || (!response.ok && response.status !== 404)) {
      this.region = null
      this.plzStatusTarget.textContent = "Die Umkreissuche ist gerade nicht erreichbar. Gerechnet wird mit dem gewählten Bundesland."
      return this.calculate()
    }
    if (response.status === 404) {
      this.region = null
      this.plzStatusTarget.textContent = `Die Postleitzahl ${plz} haben wir nicht gefunden.`
      return this.calculate()
    }

    const umgebung = await response.json()
    if (this.plzInputTarget.value !== plz) return
    if (umgebung.land) this.bundeslandSelectTarget.value = umgebung.land

    if (umgebung.zu_wenig) {
      this.region = null
      this.plzStatusTarget.textContent = `Im Umkreis von ${umgebung.radius_km} km um ${plz} ${umgebung.ort} kennen wir zu wenige Heimpreise. Gerechnet wird mit dem Durchschnitt für ${this.bundeslandName()}.`
    } else {
      this.region = umgebung
      this.plzStatusTarget.textContent = `Ø aus ${umgebung.anzahl} Pflegeheimen im Umkreis von ${umgebung.radius_km} km um ${plz} ${umgebung.ort}.`
    }
    this.calculate()
  }

  bundeslandName() {
    const select = this.bundeslandSelectTarget
    return select.options[select.selectedIndex]?.text || "Deutschland"
  }

  selectPflegegrad(event) {
    this.selectPflegegradValue(parseInt(event.currentTarget.dataset.value))
  }

  selectPflegegradValue(pg) {
    this.selectedPflegegrad = pg

    this.pflegegradButtonTargets.forEach(btn => {
      const isSelected = parseInt(btn.dataset.value) === pg
      btn.setAttribute("aria-pressed", isSelected)
      btn.classList.toggle("bg-ph24_new_blue", isSelected)
      btn.classList.toggle("text-white", isSelected)
      btn.classList.toggle("bg-ph24_new_gray_dark", !isSelected)
      btn.classList.toggle("text-ph24_new_blue", !isSelected)
      btn.classList.toggle("hover:bg-ph24_new_blue/15", !isSelected)
    })

    if (this.hasPg1NoteTarget) this.pg1NoteTarget.classList.toggle("hidden", pg !== 1)

    this.calculate()
  }

  calculate() {
    if (!this.selectedPflegegrad) return
    const data = this.dataValue
    const pg = this.selectedPflegegrad

    // Pflegeheim: Bundesland-Gesamtkosten minus Leistungszuschlag (§ 43c, ab PG 2).
    // Der Zuschlag mindert nur den pflegebedingten Anteil (EEE des Bundeslands,
    // beim Bundesdurchschnitt der gewichtete Bund-EEE) — reproduziert die
    // offiziellen vdek-Werte je Aufenthaltsjahr.
    // Mit PLZ: Ø der Heime im Umkreis (auf vdek-Stand hochgerechnet), gleiche Rechnung.
    const slug = this.bundeslandSelectTarget.value
    const durchschnitt = slug === "durchschnitt"
    const region = this.region
    const heimGesamt = region ? region.gesamt : (durchschnitt ? data.bund_eigenanteil : data.bundesland_eigenanteil[slug])
    const eee = region ? region.eee : (durchschnitt ? data.eee_durchschnitt : data.bundesland_eee[slug])

    const jahr = this.checkedValue(this.jahrRadioTargets, "1")
    const satz = pg >= 2 ? data.leistungszuschlag[jahr] : 0
    const zuschlag = Math.round(satz * eee)
    const heim = heimGesamt - zuschlag

    // 24h-Pflege: Agenturpreis minus anrechenbare Leistungen. Pflegegeld fließt
    // voll, da die Betreuungskraft keine Pflegesachleistung erbringt — außer beim
    // Umwandlungsanspruch (§ 45a Abs. 4): 40 % der Sachleistung umwidmen zählt als
    // Kombinationsleistung und kürzt das Pflegegeld anteilig auf 60 %.
    const leistungen = data.leistungen[pg] || {}
    const preis = data.preise_24h[this.checkedValue(this.qualitaetRadioTargets, "standard")].preis
    const pflegegeldVoll = pg >= 2 ? (leistungen["Pflegegeld"] || 0) : 0
    const verhinderung = pg >= 2 ? Math.round(data.verhinderungspflege_jahr / 12) : 0

    // § 45b ist zweckgebunden: zählt nur bei nach Landesrecht (§ 45a) anerkannter
    // Agentur — deshalb Opt-in statt Standardabzug.
    const entlastungAktiv = this.entlastungCheckboxTarget.checked
    const entlastung = entlastungAktiv ? (leistungen["Entlastungsbetrag"] || 0) : 0

    const umwidmungAktiv = pg >= 2 && this.umwidmungCheckboxTarget.checked
    const pflegegeld = umwidmungAktiv ? Math.round(pflegegeldVoll * 0.6) : pflegegeldVoll
    const umwidmung = umwidmungAktiv ? Math.round((leistungen["Pflegesachleistungen"] || 0) * 0.4) : 0

    const steuerAktiv = this.steuerCheckboxTarget.checked
    const steuer = steuerAktiv ? Math.round(data.steuerbonus_35a_jahr / 12) : 0
    const h24 = Math.max(0, preis - pflegegeld - verhinderung - entlastung - umwidmung - steuer)

    this.resultHeimTarget.textContent = this.formatter.format(heim)
    this.heimUebrigeTarget.textContent = this.formatter.format(heimGesamt - eee)
    this.heimPflegeTarget.textContent = this.formatter.format(eee)
    this.heimZuschlagSatzTarget.textContent = `${Math.round(satz * 100)}\u00A0%`
    // Deduction shown as a negative amount; same "−" prefix as the server render
    this.heimZuschlagTarget.textContent = zuschlag > 0 ? `−${this.formatter.format(zuschlag)}` : this.formatter.format(0)
    if (this.hasHeimLabelTarget) {
      this.heimLabelTarget.textContent = pg >= 2
        ? `Eigenanteil pro Monat ${jahr === "4" ? "ab dem 4. Jahr" : `im ${jahr}. Jahr`}`
        : "Eigenanteil pro Monat (kein Zuschlag bei PG 1)"
    }
    this.highlightBundeslandRow(slug)
    if (this.hasQuelleTarget) {
      this.quelleTarget.textContent = region
        ? `Pflegeheim: Ø von ${region.anzahl} Heimen im Umkreis von ${region.radius_km} km um ${region.plz} ${region.ort} (Preisverzeichnisse, auf vdek-Stand 01.07.2026 hochgerechnet), 24h: branchenübliche Agenturpreise, ohne Gewähr.`
        : "Durchschnittswerte lt. vdek (Stand 01.07.2026) und branchenübliche Agenturpreise, ohne Gewähr."
    }

    this.resultH24Target.textContent = this.formatter.format(h24)
    this.h24PreisTarget.textContent = this.formatter.format(preis)
    this.h24PflegegeldLabelTarget.textContent = umwidmungAktiv ? "− Pflegegeld (60 % wegen Umwandlung)" : "− Pflegegeld"
    this.h24PflegegeldTarget.textContent = this.formatter.format(pflegegeld)
    this.h24VerhinderungTarget.textContent = this.formatter.format(verhinderung)
    this.h24EntlastungRowTarget.classList.toggle("hidden", !entlastungAktiv)
    this.h24EntlastungTarget.textContent = this.formatter.format(entlastung)
    this.h24UmwidmungRowTarget.classList.toggle("hidden", !umwidmungAktiv)
    this.h24UmwidmungTarget.textContent = this.formatter.format(umwidmung)
    this.h24SteuerRowTarget.classList.toggle("hidden", !steuerAktiv)
    this.h24SteuerTarget.textContent = this.formatter.format(steuer)

    const max = Math.max(heim, h24, 1)
    this.barHeimTarget.style.width = `${(heim / max) * 100}%`
    this.barH24Target.style.width = `${(h24 / max) * 100}%`

    if (this.hasStickyHeimTarget) this.stickyHeimTarget.textContent = this.formatter.format(heim)
    if (this.hasStickyH24Target) this.stickyH24Target.textContent = this.formatter.format(h24)

    const diff = heim - h24
    if (Math.abs(diff) < 1) {
      this.deltaTarget.textContent = "Pflegeheim und 24h-Pflege zu Hause kosten in diesem Szenario etwa gleich viel."
    } else {
      this.deltaTarget.textContent = diff > 0
        ? `Die 24h-Pflege zu Hause ist in diesem Szenario ${this.formatter.format(diff)} pro Monat günstiger als das Pflegeheim.`
        : `Das Pflegeheim ist in diesem Szenario ${this.formatter.format(Math.abs(diff))} pro Monat günstiger als die 24h-Pflege zu Hause.`
    }
  }

  // The Bundesland table sits outside this controller; mark the row the result is based on
  highlightBundeslandRow(slug) {
    document.querySelectorAll("[data-pflegekosten-bundesland]").forEach(row => {
      row.classList.toggle("bg-ph24_new_blue/10", row.dataset.pflegekostenBundesland === slug)
    })
  }

  checkedValue(radios, fallback) {
    return radios.find(r => r.checked)?.value || fallback
  }
}
